import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Alert, Select, Spin, Tag, message } from "antd";

import apiClient from "../api/apiClient";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import {
  DAILY_RECAP_BRIEFING_OPTIONS,
  type DailyRecapBriefingChoice,
  type DailyRecapBriefingPreference,
} from "../types/dailyRecapBriefing";
import "./DailyRecapBriefingSettings.css";

interface PreferenceResponse {
  eligible: boolean;
  preference: DailyRecapBriefingPreference;
}

const formatPausedUntil = (value: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);
};

const DailyRecapBriefingSettings: React.FC = () => {
  const { user } = useAuth();
  const { selectedCompany } = useSelection();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [preference, setPreference] =
    useState<DailyRecapBriefingPreference | null>(null);
  const [loadError, setLoadError] = useState(false);
  const companyId = Number(selectedCompany) || null;

  const loadPreference = useCallback(async () => {
    if (!user?.id || !companyId) {
      setPreference(null);
      setEligible(false);
      return;
    }

    setLoading(true);
    setLoadError(false);
    try {
      const response = await apiClient.get<PreferenceResponse>(
        "/api/daily-recap-briefing/preference",
        {
          params: { company_id: companyId },
          skipGlobalLoading: true,
        }
      );
      setEligible(response.data.eligible);
      setPreference(response.data.preference);
    } catch (_error) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [companyId, user?.id]);

  useEffect(() => {
    void loadPreference();
  }, [loadPreference]);

  const updatePreference = async (choice: DailyRecapBriefingChoice) => {
    if (!companyId || !preference?.can_manage) return;
    setSaving(true);
    try {
      const response = await apiClient.put<{
        preference: DailyRecapBriefingPreference;
      }>(
        "/api/daily-recap-briefing/preference",
        { company_id: companyId, choice },
        { skipGlobalLoading: true }
      );
      setPreference(response.data.preference);
      message.success("Préférence du briefing mise à jour");
    } catch (error) {
      message.error(
        (error as any)?.response?.data?.error ||
          "Impossible de modifier le briefing de clôture"
      );
    } finally {
      setSaving(false);
    }
  };

  const status = useMemo(() => {
    if (!preference || preference.mode === "daily") {
      return { color: "green", label: "Chaque jour", icon: <CalendarOutlined /> };
    }
    if (preference.mode === "paused") {
      return { color: "gold", label: "Suspendu", icon: <ClockCircleOutlined /> };
    }
    return { color: "default", label: "Désactivé", icon: <StopOutlined /> };
  }, [preference]);

  return (
    <section className="daily-recap-briefing-settings" aria-live="polite">
      <div className="daily-recap-briefing-settings__heading">
        <div>
          <h3>Briefing de clôture</h3>
          <p>Affichage automatique du dernier bilan à votre première ouverture.</p>
        </div>
        {preference ? (
          <Tag color={status.color} icon={status.icon}>
            {status.label}
          </Tag>
        ) : null}
      </div>

      {!companyId ? (
        <Alert
          type="info"
          showIcon
          message="Sélectionnez une entreprise pour gérer ce réglage."
        />
      ) : (
        <Spin spinning={loading || saving} tip="Chargement...">
          {loadError ? (
            <Alert
              type="warning"
              showIcon
              message="Le réglage du briefing est temporairement indisponible."
            />
          ) : preference ? (
            <>
              {!preference.can_manage ? (
                <Alert
                  type={eligible ? "info" : "warning"}
                  showIcon
                  message={
                    eligible
                      ? "Le briefing est activé par défaut. Ce réglage sera modifiable après sa première présentation."
                      : "Vous n’avez actuellement pas accès au bilan de clôture pour cette entreprise."
                  }
                />
              ) : (
                <div className="daily-recap-briefing-settings__control">
                  <label htmlFor="daily-recap-briefing-frequency">
                    Fréquence d’affichage automatique
                  </label>
                  <Select<DailyRecapBriefingChoice>
                    id="daily-recap-briefing-frequency"
                    value={preference.choice}
                    options={DAILY_RECAP_BRIEFING_OPTIONS}
                    disabled={saving}
                    onChange={(choice) => void updatePreference(choice)}
                    popupMatchSelectWidth={false}
                  />
                  {preference.mode === "paused" && preference.paused_until ? (
                    <small>
                      Reprise automatique le {formatPausedUntil(preference.paused_until)}.
                    </small>
                  ) : null}
                </div>
              )}

              {preference.can_manage && !eligible ? (
                <Alert
                  className="daily-recap-briefing-settings__access-note"
                  type="warning"
                  showIcon
                  message="La préférence est conservée, mais aucun briefing ne sera affiché tant que l’accès au bilan n’est pas rétabli."
                />
              ) : null}
            </>
          ) : null}
        </Spin>
      )}

      <div className="daily-recap-briefing-settings__note">
        Ce choix agit uniquement sur la fenêtre automatique. Les bilans restent
        consultables dans la cloche et l’Agenda.
      </div>
    </section>
  );
};

export default DailyRecapBriefingSettings;
